import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only Gold Coast Referral Sender — STANDALONE top-level document.
 *
 * The artifact ships its own runtime (Google Sign-In, Gmail API, native HTML5
 * drag-and-drop). For drag-and-drop to work reliably and for the artifact
 * to "own" the document, we serve it as the top-level page at /referrals
 * rather than embedding it in an iframe or rendering it inside the GCOMS
 * Layout chrome.
 *
 * Flow:
 *   1) Authenticated admin reaches /referrals (gated by RequireAuth).
 *   2) We ask the backend for a 60-second one-shot JWT.
 *   3) We `fetch(/api/referrals-secure?t=…, { credentials: "include" })`,
 *      which (a) streams the artifact's HTML byte-for-byte and (b) sets
 *      the `rs_admin` httpOnly cookie that the artifact's own fetches to
 *      /api/doctors and /api/generate will automatically send.
 *   4) We replace the entire current document with the returned HTML via
 *      `document.open() / document.write() / document.close()`. Scripts
 *      inside the HTML execute during parsing, so Google Sign-In and the
 *      artifact's UI initialize natively. The URL bar stays at /referrals.
 *
 * Trade-off: once document.write() runs, the React app on this tab is
 * gone. The user uses the browser back button to leave the artifact.
 */
export default function ReferralSender() {
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // Guard against StrictMode double-invoke.
    startedRef.current = true;

    (async () => {
      try {
        // 1) Mint short-lived admin token (Bearer auth).
        const { data } = await api.get("/admin/referrals-sender-token");
        // 2) Fetch the HTML AND let the backend set the rs_admin cookie.
        const res = await fetch(
          `${BACKEND}/api/referrals-secure?t=${encodeURIComponent(data.token)}`,
          { credentials: "include" },
        );
        if (!res.ok) {
          if (res.status === 403) setError("Admin access required to load the referral sender.");
          else if (res.status === 404) setError("The referral sender file is not installed on the server.");
          else setError(`Could not load the referral sender (HTTP ${res.status}).`);
          return;
        }
        const html = await res.text();

        // Inject a tiny client-side "back to GCOMS.RN" pill before
        // document.write. The injection lives only in the rendered DOM —
        // we do NOT modify the file on disk (still byte-for-byte from
        // /api/referrals-secure). Without this, the artifact becomes the
        // whole document and there's no in-page way to leave it.
        const backNav =
          `<a href="/dashboard" id="gcoms-back-to-dashboard" ` +
          `style="position:fixed;top:14px;left:14px;z-index:2147483647;` +
          `display:inline-flex;align-items:center;gap:6px;` +
          `padding:6px 12px;border-radius:9999px;` +
          `background:rgba(255,255,255,0.92);color:#1a1a1a;` +
          `text-decoration:none;font:500 12px -apple-system,` +
          `BlinkMacSystemFont,'Segoe UI',sans-serif;` +
          `border:1px solid rgba(0,0,0,0.08);` +
          `box-shadow:0 2px 8px rgba(0,0,0,0.06);` +
          `backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);` +
          `transition:background .15s,box-shadow .15s;" ` +
          `onmouseover="this.style.background='rgba(255,255,255,1)';` +
          `this.style.boxShadow='0 4px 14px rgba(0,0,0,0.10)';" ` +
          `onmouseout="this.style.background='rgba(255,255,255,0.92)';` +
          `this.style.boxShadow='0 2px 8px rgba(0,0,0,0.06)';">` +
          `<span style="font-size:14px;line-height:1">←</span>` +
          `<span>GCOMS.RN</span></a>`;
        const closeIdx = html.toLowerCase().lastIndexOf("</body>");

        // Back-button handler. document.write() replaces the React app, so
        // there's no router left to intercept history navigation. We push
        // an extra history entry on top of /referrals and add a popstate
        // listener inside the artifact's document — when the user clicks
        // browser-back, popstate fires and we force a real navigation back
        // to /dashboard. Pairs with the visible "← GCOMS.RN" pill so both
        // exit paths work consistently.
        const backHandler =
          `<script>(function(){` +
          `try{history.pushState({_gcomsExit:1},"","/referrals");}catch(e){}` +
          `window.addEventListener("popstate",function(){` +
          `window.location.replace("/dashboard");` +
          `});` +
          `})();</script>`;

        // Auto-select the referring doctor when a treatment note is pasted.
        // Injected as an external script tag pointing to
        // /api/referrals-auto-doctor.js — the file lives at
        // /app/backend/static/auto-doctor.js so we can iterate on it without
        // fighting template-literal escaping. A `?v=` cache-buster forces the
        // browser to fetch a fresh copy each pageload.
        const smartSelect =
          `<script src="/api/referrals-auto-doctor.js?v=${Date.now()}"></script>`;

        const finalHtml =
          closeIdx > 0
            ? html.slice(0, closeIdx) + backNav + backHandler + smartSelect + html.slice(closeIdx)
            : html + backNav + backHandler + smartSelect;

        // Replace the entire document. document.write() triggers an
        // implicit document.open() that clears React's tree, then
        // parses the new HTML — executing <script> tags in order.
        // Using innerHTML would NOT execute scripts; using an iframe
        // would break native drag-and-drop semantics.
        document.open();
        document.write(finalHtml);
        document.close();
      } catch (e) {
        setError("Could not load the referral sender. Please try again.");
      }
    })();
  }, []);

  if (error) {
    return (
      <div
        data-testid="referral-sender-error"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div style={{ maxWidth: 480, display: "flex", gap: 12 }}>
          <AlertTriangle size={20} color="#b91c1c" />
          <div style={{ fontSize: 14, color: "#1a1a1a" }}>
            {error}
            <div style={{ marginTop: 12 }}>
              <a href="/dashboard" style={{ color: "#F26A24" }}>← Back to GCOMS.RN</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="referral-sender-loading"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        color: "#666",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <Loader2 size={28} className="animate-spin" />
      <div style={{ fontSize: 13 }}>Loading referral sender…</div>
    </div>
  );
}
